import React, { useState, useEffect } from 'react';
import { App, Table, Button, Space, Modal, Form, Input, Select, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';

interface Category {
  id: string;
  name: string;
  handle: string;
  parent_category_id: string | null;
  is_active?: boolean;
  is_internal?: boolean;
  parent_category?: {
    name: string;
  };
  children?: Category[];
}

const Categories: React.FC = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/product-categories');
      
      // A API retorna { product_categories: [...], count, offset, limit }
      // Filtrar apenas categorias pai (sem parent_category_id)
      const allCategories = response.data.product_categories || [];
      const parentCategories = allCategories.filter((cat: Category) => !cat.parent_category_id);
      
      // Adicionar children a cada categoria pai
      const categoriesWithChildren = parentCategories.map((parent: Category) => ({
        ...parent,
        children: allCategories.filter((cat: Category) => cat.parent_category_id === parent.id)
      }));
      
      setCategories(categoriesWithChildren);
    } catch (error: any) {
      message.error(error.response?.data?.message || t('categories.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCategory(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    form.setFieldsValue({
      name: category.name,
      handle: category.handle,
      parent_category_id: category.parent_category_id,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await apiClient.delete(`/admin/product-categories/${id}`);
      message.success(t('categories.deleteSuccess'));
      loadCategories();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('categories.deleteError'));
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      // Gerar handle automaticamente a partir do nome
      const payload = {
        ...values,
        handle: values.name.toLowerCase().trim(),
        is_active: true,
        is_internal: false
      };
      
      if (editingCategory) {
        await apiClient.post(`/admin/product-categories/${editingCategory.id}`, payload);
        message.success(t('categories.updateSuccess'));
      } else {
        await apiClient.post('/admin/product-categories', payload);
        message.success(t('categories.createSuccess'));
      }
      
      setModalVisible(false);
      loadCategories();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('categories.saveError'));
    }
  };

  const columns = [
    {
      title: t('common.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('common.status'),
      dataIndex: 'is_active',
      key: 'status',
      width: 100,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
    },
    {
      title: t('categories.visibility'),
      dataIndex: 'is_internal',
      key: 'visibility',
      width: 100,
      render: (isInternal: boolean) => (
        <Tag color={isInternal ? 'default' : 'green'}>
          {isInternal ? t('common.private') : t('common.public')}
        </Tag>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 150,
      render: (_: any, record: Category) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            {t('common.edit')}
          </Button>
          <Popconfirm
            title={t('categories.confirmDelete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              {t('common.delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, color: '#334E3B', margin: 0 }}>
          {t('categories.title')}
        </h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          {t('categories.create')}
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={categories}
        loading={loading}
        rowKey="id"
        defaultExpandAllRows={true}
      />

      <Modal
        title={editingCategory ? t('categories.edit') : t('categories.create')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label={t('common.name')}
            rules={[{ required: true, message: t('categories.nameRequired') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="parent_category_id"
            label={t('categories.parent')}
          >
            <Select allowClear placeholder={t('categories.selectParent')}>
              {Array.isArray(categories) && categories.map(cat => (
                <Select.Option key={cat.id} value={cat.id}>
                  {cat.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setModalVisible(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="primary" htmlType="submit">
                {editingCategory ? t('common.update') : t('common.create')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Categories;
